/*
  HLL visual pass: "What they say about Deme" on the product page.

  Rebuilds the Judge.me featured reviews carousel into a text led layout:
  a heading with the overall rating, then a row of reviews that drifts
  sideways on its own. The review text leads. The reviewed product sits
  small under each review and links to it.

  The row pauses while the pointer is over it, for a few seconds after a
  touch, and while it is off screen. It can be dragged at any time. With
  "reduce motion" switched on it does not move by itself.

  The reviews, stars, product photos and the rating summary all come from the
  Judge.me block already on the page. Nothing is hardcoded here. If this
  script or Flickity is missing, the original Judge.me carousel stays as is.

  To remove: delete the script tag in layout/theme.liquid and section 10 of
  assets/hll-visual-pass.css.
*/
(function () {
  'use strict';

  function text(root, selector) {
    var el = root.querySelector(selector);
    return el ? el.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function el(tag, className, content) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (content) node.textContent = content;
    return node;
  }

  // Judge.me gives a square 70px thumbnail. Ask Shopify's CDN for the
  // uncropped photo at a size that stays sharp at 40px wide.
  function largeImage(src) {
    return src ? src.replace(/_\d+x\d+(\.[a-z0-9]+)(\?|$)/i, '_160x$1$2') : '';
  }

  // "45_70x70.png?v=1" -> "45.png", so two sizes of one photo compare equal
  function fileName(src) {
    return (src || '')
      .split('?')[0]
      .split('/')
      .pop()
      .replace(/_\d+x\d*(\.[a-z0-9]+)$/i, '$1');
  }

  /*
    Judge.me gives the product name and photo for each review, but not its
    link. Ask Shopify's own search for the product by name, and only link
    when the name matches exactly and the photo is the same file. If either
    check fails the card simply stays unlinked. Results are kept for the
    visit so each product is looked up once.
  */
  var LINK_CACHE = 'hllReviewLinks';

  function readCache() {
    try {
      return JSON.parse(window.sessionStorage.getItem(LINK_CACHE)) || {};
    } catch (error) {
      return {};
    }
  }

  function writeCache(cache) {
    try {
      window.sessionStorage.setItem(LINK_CACHE, JSON.stringify(cache));
    } catch (error) {}
  }

  function findProductUrl(review) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url =
      root +
      'search/suggest.json?q=' +
      encodeURIComponent(review.product) +
      '&resources[type]=product&resources[limit]=6&resources[options][fields]=title';
    return window
      .fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (json) {
        var products = (json && json.resources && json.resources.results && json.resources.results.products) || [];
        var wanted = review.product.toLowerCase();
        for (var i = 0; i < products.length; i++) {
          var product = products[i];
          var sameName = String(product.title || '').trim().toLowerCase() === wanted;
          var samePhoto = fileName(product.image || (product.featured_image && product.featured_image.url)) === review.file;
          if (sameName && samePhoto && product.url) return String(product.url).split('?')[0];
        }
        return '';
      })
      .catch(function () {
        return '';
      });
  }

  function build(wrapper) {
    var items = Array.prototype.slice.call(wrapper.querySelectorAll('.jdgm-carousel-item'));
    var reviews = items
      .map(function (item) {
        var img = item.querySelector('.jdgm-carousel-item__product-image');
        var src = img ? img.getAttribute('data-src-retina') || img.getAttribute('data-src') || img.getAttribute('src') : '';
        return {
          stars: item.querySelector('.jdgm-carousel-item__review-rating'),
          title: text(item, '.jdgm-carousel-item__review-title'),
          body: text(item, '.jdgm-carousel-item__review-body'),
          name: text(item, '.jdgm-carousel-item__reviewer-name'),
          product: img ? (img.getAttribute('alt') || '').trim() : '',
          file: fileName(src),
          image: largeImage(src),
          fallback: src
        };
      })
      .filter(function (review) {
        return review.image && (review.body || review.title);
      });

    if (reviews.length < 3) return;

    var heading = text(wrapper, '.jdgm-carousel-title') || 'What they say';
    var section = el('section', 'hll-reviews');
    section.setAttribute('aria-label', heading);

    var header = el('div', 'hll-reviews__header');
    // Styled with the other section titles on this page (section 11 of the CSS)
    var titleEl = el('h2', 'section-header__title', heading);
    header.appendChild(titleEl);

    // Overall rating, copied from the Judge.me header
    var rating = wrapper.querySelector('.jdgm-all-reviews-rating');
    var count = text(wrapper, '.jdgm-carousel-number-of-reviews');
    if (rating || count) {
      var summary = el('div', 'hll-reviews__summary');
      if (rating) {
        var score = parseFloat(rating.getAttribute('data-score'));
        if (score) {
          // One star, then "4.9 from 168 reviews". The star is Judge.me's own
          // star glyph, the same one used on the cards below.
          var star = el('span', 'hll-reviews__stars hll-reviews__summary-star');
          star.setAttribute('aria-hidden', 'true');
          star.appendChild(el('span', 'jdgm-star jdgm--on'));
          summary.appendChild(star);
          count = (Math.round(score * 10) / 10).toFixed(1) + ' ' + count;
        }
      }
      summary.appendChild(el('span', '', count));
      header.appendChild(summary);
    }
    section.appendChild(header);

    var slider = el('div', 'hll-reviews__slider');
    var cards = [];

    reviews.forEach(function (review) {
      var cell = el('div', 'hll-reviews__cell');
      var card = el('figure', 'hll-reviews__card');

      if (review.stars) {
        var stars = review.stars.cloneNode(true);
        stars.className = 'hll-reviews__stars';
        card.appendChild(stars);
      }
      if (review.title) card.appendChild(el('p', 'hll-reviews__title', review.title));
      if (review.body) card.appendChild(el('blockquote', 'hll-reviews__body', review.body));

      // Foot of the card: small product photo, reviewer, product name.
      // It is an <a> with no href until the product page has been found.
      var foot = el('a', 'hll-reviews__product');
      var img = document.createElement('img');
      img.alt = '';
      img.width = 40;
      img.height = 60;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.onerror = function () {
        img.onerror = null;
        img.src = review.fallback;
      };
      img.src = review.image;
      foot.appendChild(img);

      var by = el('figcaption', 'hll-reviews__by');
      if (review.name) by.appendChild(el('span', 'hll-reviews__name', review.name));
      if (review.product) {
        var about = el('span', 'hll-reviews__about', 'Review of ');
        about.appendChild(el('span', 'hll-reviews__product-name', review.product));
        by.appendChild(about);
      }
      foot.appendChild(by);
      card.appendChild(foot);

      cell.appendChild(card);
      slider.appendChild(cell);
      cards.push(card);
      review.foot = foot;
    });

    section.appendChild(slider);

    var host = wrapper.parentElement;
    host.parentElement.insertBefore(section, host);
    wrapper.classList.add('hll-reviews-ready');

    // Judge.me first prints its default title ("Let customers speak for us")
    // and swaps in the shop's own title a moment later. If this section was
    // built in between, it kept the default. Follow the original title so
    // the two always match.
    function syncTitle() {
      var current = text(wrapper, '.jdgm-carousel-title');
      if (!current || current === titleEl.textContent) return;
      titleEl.textContent = current;
      section.setAttribute('aria-label', current);
    }

    if ('MutationObserver' in window) {
      new MutationObserver(syncTitle).observe(wrapper.querySelector('.jdgm-carousel-title-and-link') || wrapper, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }
    syncTitle();

    // Reviews differ in length. The row is as tall as the longest one.
    function sizeRow() {
      // Cards are stretched to --hll-card-h, so clear it to read their own height
      section.style.setProperty('--hll-card-h', '0px');
      var tallest = 0;
      cards.forEach(function (card) {
        tallest = Math.max(tallest, card.offsetHeight);
      });
      if (tallest) section.style.setProperty('--hll-card-h', tallest + 'px');
    }

    sizeRow();

    var flkty = new window.Flickity(slider, {
      cellAlign: 'center',
      wrapAround: true,
      freeScroll: true,
      pageDots: false,
      prevNextButtons: false,
      dragThreshold: 8
    });

    var resizeTimer;
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(sizeRow, 150);
    });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(sizeRow);

    /* Drift: the row moves left at a steady, slow speed. */
    // Pixels per second. A phone shows about one review at a time, so the
    // same speed looks slower there than on a wide screen.
    // The speed follows the device, not the window width: a touch screen
    // gets the phone speed, anything with a mouse gets the slow speed. A
    // narrow browser window on a laptop therefore stays slow.
    var SPEED_MOUSE = 22;
    var SPEED_TOUCH = 55;
    var touch = window.matchMedia('(hover: none) and (pointer: coarse)');
    var TOUCH_PAUSE = 6000; // ms the row waits after a touch or drag
    var calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var hovered = false;
    var held = false;
    var onScreen = true;
    var lastFrame = 0;
    var holdTimer;

    function frame(now) {
      var elapsed = lastFrame ? Math.min(now - lastFrame, 100) : 0;
      lastFrame = now;
      if (elapsed && !hovered && !held && onScreen && !flkty.isAnimating && !flkty.isPointerDown) {
        var speed = touch.matches ? SPEED_TOUCH : SPEED_MOUSE;
        flkty.x = (flkty.x - (speed * elapsed) / 1000) % flkty.slideableWidth;
        flkty.positionSlider();
      }
      window.requestAnimationFrame(frame);
    }

    function hold() {
      held = true;
      window.clearTimeout(holdTimer);
    }

    function release() {
      window.clearTimeout(holdTimer);
      holdTimer = window.setTimeout(function () {
        held = false;
      }, TOUCH_PAUSE);
    }

    if (!calm && typeof flkty.positionSlider === 'function' && flkty.slideableWidth) {
      slider.addEventListener('mouseenter', function () {
        hovered = true;
      });
      slider.addEventListener('mouseleave', function () {
        hovered = false;
      });
      slider.addEventListener('focusin', hold);
      slider.addEventListener('focusout', release);
      flkty.on('pointerDown', hold);
      flkty.on('pointerUp', release);

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          onScreen = entries[entries.length - 1].isIntersecting;
        }).observe(section);
      }

      window.requestAnimationFrame(frame);
    }

    function applyLink(review, url) {
      if (!url || url === window.location.pathname) return;
      review.foot.href = url;
      review.foot.setAttribute('aria-label', 'View ' + review.product);
    }

    // Look the links up only when the section is close to the screen,
    // two at a time, starting with the review that is showing.
    function linkProducts() {
      var cache = readCache();
      var queue = [];
      reviews.forEach(function (review) {
        if (!review.product) return;
        if (Object.prototype.hasOwnProperty.call(cache, review.product)) applyLink(review, cache[review.product]);
        else queue.push(review);
      });

      function next() {
        var review = queue.shift();
        if (!review) return;
        if (Object.prototype.hasOwnProperty.call(cache, review.product)) {
          applyLink(review, cache[review.product]);
          next();
          return;
        }
        findProductUrl(review).then(function (url) {
          cache[review.product] = url;
          writeCache(cache);
          applyLink(review, url);
          next();
        });
      }

      next();
      next();
    }

    function linkWhenNear() {
      var rect = section.getBoundingClientRect();
      if (rect.top > window.innerHeight + 800 || rect.bottom < -800) return;
      window.removeEventListener('scroll', linkWhenNear);
      linkProducts();
    }

    window.addEventListener('scroll', linkWhenNear, { passive: true });
    linkWhenNear();
  }

  function boot() {
    if (!document.body.classList.contains('template-product')) return;
    var tries = 0;
    (function look() {
      var wrapper = document.querySelector('.jdgm-carousel-wrapper');
      if (wrapper && wrapper.classList.contains('hll-reviews-ready')) return;
      if (wrapper && wrapper.querySelector('.jdgm-carousel-item') && window.Flickity) {
        build(wrapper);
        return;
      }
      // Judge.me fills the block in after the page loads, so keep looking for 20s
      if (++tries < 80) window.setTimeout(look, 250);
    })();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
